// frontend/src/components/Facturas.jsx

import React, { useEffect, useMemo, useState } from "react";
import "./Clientes.css";
import "../styles/theme-dark.css";
import EmailFacturaModal from "./EmailFacturaModal";
import EditarFacturaModal from "./EditarFacturaModal";

const API = "/api";


// normalizador
const norm = (s = "") =>
  (s + "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// formatear euros
const formatEuro = (number) => {
  const num = Number(number) || 0;
  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

export default function Facturas() {
  const [facturas, setFacturas] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const [company, setCompany] = useState(null);
  const [emailSettings, setEmailSettings] = useState(null);

  const [openEmail, setOpenEmail] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);

  // ✅ Estados del modal Editar Factura
  const [openEdit, setOpenEdit] = useState(false);
  const [facturaEditar, setFacturaEditar] = useState(null);

  // ==============================
  //   CARGA INICIAL
  // ==============================
  useEffect(() => {
    loadFacturas();
    loadCompany();
    loadEmailSettings();
  }, []);

  async function loadFacturas() {
    try {
      setLoading(true);
      const res = await fetch(`${API}/facturas`);
      const json = await res.json();

      if (json.success) {
        const facturasProcesadas = (json.data || []).map((f) => {
          let estado = f.estado || "pendiente";
          if (f.tipo === "abono") estado = "abono";
          return { ...f, estadoFactura: estado };
        });

        setFacturas(facturasProcesadas);
      } else {
        console.error("Error cargando facturas:", json.error);
      }
    } catch (err) {
      console.error("Error cargando facturas:", err);
    } finally {
      setLoading(false);
    }
  }

  async function loadCompany() {
    try {
      const res = await fetch(`${API}/companies`);
      const json = await res.json();
      const data = json?.data || json;
      if (Array.isArray(data)) {
        setCompany(data[0] || null);
      } else {
        setCompany(data || null);
      }
    } catch (err) {
      console.error("Error cargando empresa:", err);
    }
  }

  async function loadEmailSettings() {
    try {
      const res = await fetch(`${API}/email-settings`);
      const json = await res.json();
      setEmailSettings(json.data || {});
    } catch (err) {
      console.error("Error cargando email-settings:", err);
    }
  }

  // ==============================
  //   FILTRADO
  // ==============================
  const filtered = useMemo(() => {
    if (!query) return facturas;
    const q = norm(query);

    return facturas.filter((f) => {
      const c = f.cliente || {};
      const empresa = f.empresa || {};

      return (
        norm(f.numero).includes(q) ||
        norm(c.nombre || c.nombre_empresa || "").includes(q) ||
        norm(f.estadoFactura || "").includes(q) ||
        norm(empresa.nombre || "").includes(q) ||
        (f.fechaEmision &&
          norm(new Date(f.fechaEmision).toLocaleDateString("es-ES")).includes(q))
      );
    });
  }, [facturas, query]);

  // ==============================
  //   ACCIONES
  // ==============================
  function abrirEmail(factura) {
    setSelectedFactura(factura);
    setOpenEmail(true);
  }

  function verPDF(factura) {
    const mostrarIBAN = window.confirm(
      "¿Quieres mostrar el número de cuenta en la factura?"
    );
    const ibanFlag = mostrarIBAN ? 1 : 0;

    window.open(
      `${API}/facturas/${factura.id}/pdf?iban=${ibanFlag}`,
      "_blank"
    );
  }

  async function marcarPagada(factura) {
    try {
      const res = await fetch(
        `${API}/facturas/${factura.id}/marcar-pagada`,
        { method: "PUT" }
      );

      if (!res.ok) throw new Error("No se pudo marcar como pagada");

      await loadFacturas();
    } catch (err) {
      alert("Error marcando pagada: " + err.message);
    }
  }

  async function crearAbono(factura) {
    if (!window.confirm(`¿Crear abono de la factura ${factura.numero}?`)) return;

    try {
      const res = await fetch(`${API}/facturas/${factura.id}/abono`, {
        method: "POST",
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || "No se pudo crear el abono");
      }

      alert("Abono creado correctamente");
      await loadFacturas();
    } catch (err) {
      alert("Error creando abono: " + err.message);
    }
  }

  

  // ✅ Función para abrir el modal de edición
  function abrirEditarFactura(factura) {
    setFacturaEditar(factura);
    setOpenEdit(true);
  }

  async function eliminarFactura(id) {
    if (!window.confirm("¿Seguro que quieres eliminar esta factura?"))
      return;

    try {
      const res = await fetch(`${API}/facturas/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("No se pudo eliminar la factura");

      await loadFacturas();
    } catch (err) {
      alert("Error eliminando factura: " + err.message);
    }
  }

  // ==============================
  //   RENDER
  // ==============================
  return (
    <>
      <style>{`
        .facturas-table th,
        .facturas-table td {
          font-size: 12px;
          padding: 8px 10px;
        }

        .estado-pill {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          border-radius: 999px;
          font-size: 11px;
          font-weight: 500;
        }
        .estado-pill--pendiente {
          background: rgba(234,179,8,0.12);
          color: #fde68a;
        }
        .estado-pill--pagada {
          background: rgba(34,197,94,0.18);
          color: #bbf7d0;
        }
        .estado-pill--abono {
          background: rgba(147, 51, 234, 0.25);
          color: #e9d5ff;
        }
      `}</style>

      <div className="page-dark">

        {/* HEADER */}
        <header
          style={{
            padding: "20px 30px",
            marginBottom: 25,
            background: "rgba(15,23,42,0.65)",
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            backdropFilter: "blur(10px)",
            borderRadius: "0 0 16px 16px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.45)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <img src="/corex-logo.png" alt="COREX" style={{ height: 40 }} />
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700 }}>
                Facturas
              </h1>
            </div>

            <button
              className="glass-btn"
              onClick={() => window.history.back()}
              style={{ fontSize: 14 }}
            >
              ← Volver
            </button>
          </div>

          <p style={{ opacity: 0.7, marginTop: 6 }}>
            Gestiona tus facturas, envíalas por email y controla su estado de cobro.
          </p>
        </header>

        {/* FILTROS */}
        <div
          className="glass-box"
          style={{
            marginBottom: 18,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 14,
          }}
        >
          <div style={{ flex: 1 }}>
            <input
              className="glass-input"
              type="search"
              placeholder="Buscar por número, cliente, estado o fecha…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div style={{ fontSize: 12, marginTop: 4, opacity: 0.7 }}>
              {filtered.length} factura
              {filtered.length !== 1 ? "s" : ""} mostradas
              {query && (
                <>
                  {" "}
                  — filtrando por <strong>{query}</strong>
                </>
              )}
            </div>
          </div>
        </div>

        {/* TABLA */}
        <div className="glass-box" style={{ padding: 0 }}>
          <div
            style={{
              maxHeight: "70vh",
              overflow: "auto",
              borderRadius: 16,
            }}
          >
            <table
              className="facturas-table"
              style={{ width: "100%", borderCollapse: "collapse" }}
            >
              <thead>
                <tr>
                  <th style={{ width: 100 }}>Nº</th>
                  <th style={{ width: 100 }}>Fecha</th>
                  <th>Cliente</th>
                  <th style={{ width: 80 }}>Estado</th>
                  <th style={{ width: 120, textAlign: "right" }}>Base</th>
                  <th style={{ width: 120, textAlign: "right" }}>Total</th>
                  <th style={{ width: 160, textAlign: "center" }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((f) => {
                  const cliente = f.cliente || {};
                  const fecha = f.fechaEmision
                    ? new Date(f.fechaEmision).toLocaleDateString("es-ES")
                    : "";

                  const estado = f.estadoFactura || "pendiente";

                  let estadoClass = "estado-pill--pendiente";
                  if (estado === "pagada") estadoClass = "estado-pill--pagada";
                  if (estado === "abono") estadoClass = "estado-pill--abono";

                  return (
                    <tr key={f.id}>
                      <td>
                        <strong style={{ fontSize: 12 }}>
                          {f.numero || f.id}
                        </strong>
                      </td>

                      <td style={{ fontSize: 12 }}>{fecha}</td>

                      <td
                        style={{
                          fontSize: 12,
                          maxWidth: 180,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {cliente.nombre_empresa ||
                          cliente.nombre ||
                          "-"}
                      </td>

                      <td style={{ fontSize: 11 }}>
                        <span className={`estado-pill ${estadoClass}`}>
                          {estado}
                        </span>
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          textAlign: "right",
                          opacity: 0.8,
                        }}
                      >
                        {formatEuro(f.baseImponible)} €
                      </td>

                      <td
                        style={{
                          fontSize: 12,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {formatEuro(f.total)} €
                      </td>

                      <td style={{ textAlign: "center" }}>
                        <div className="action-buttons-compact">

                          {/* Enviar email */}
                          <button
                            className="glass-btn icon-btn"
                            onClick={() => abrirEmail(f)}
                            title="Enviar por email"
                          >
                            ✉
                          </button>

                          {/* Ver PDF */}
                          <button
                            className="glass-btn icon-btn"
                            onClick={() => verPDF(f)}
                            title="Ver PDF"
                          >
                            📄
                          </button>

                          {/* Marcar pagada */}
                          <button
                            className="glass-btn icon-btn"
                            onClick={() => marcarPagada(f)}
                            title="Marcar como pagada"
                          >
                            💶
                          </button>

                          {/* Crear abono */}
                          <button
                            className="glass-btn icon-btn"
                            onClick={() => crearAbono(f)}
                            title="Crear abono"
                          >
                            🔄
                          </button>

                          {/* Editar factura */}
                          <button
                            className="glass-btn icon-btn"
                            onClick={() => abrirEditarFactura(f)}
                            title="Editar factura"
                          >
                            ✏️
                          </button>

                          {/* Eliminar */}
                          <button
                            className="glass-btn icon-btn"
                            onClick={() => eliminarFactura(f.id)}
                            style={{ background: "rgba(239,68,68,0.2)" }}
                            title="Eliminar"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {filtered.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 20, opacity: 0.6 }}>
                      No hay facturas que coincidan.
                    </td>
                  </tr>
                )}

                {loading && (
                  <tr>
                    <td colSpan={7} style={{ textAlign: "center", padding: 20, opacity: 0.6 }}>
                      Cargando facturas…
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* MODAL EMAIL FACTURA */}
        <EmailFacturaModal
          isOpen={openEmail}
          onClose={() => {
            setOpenEmail(false);
            setSelectedFactura(null);
          }}
          factura={selectedFactura}
          empresa={company}
          emailSettings={emailSettings}
          onSend={async ({ to, cc, bcc, subject, body }) => {
            if (!selectedFactura) return;
            try {
              const res = await fetch(
                `${API}/facturas/${selectedFactura.id}/enviar-email`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    emailDestino: to,
                    cc,
                    bcc,
                    asunto: subject,
                    mensaje: body,
                  }),
                }
              );

              if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || "No se pudo enviar el correo");
              }

              alert("Factura enviada por email correctamente.");
              setOpenEmail(false);
              setSelectedFactura(null);
              await loadFacturas();
            } catch (err) {
              alert("Error enviando email: " + err.message);
            }
          }}
        />

        {/* MODAL EDITAR FACTURA */}
        <EditarFacturaModal
          open={openEdit}
          onClose={() => setOpenEdit(false)}
          factura={facturaEditar}
          onSaved={loadFacturas}
        />
      </div>
    </>
  );
}
